import React, { useEffect, useMemo, useState } from 'react';
import {
  createPromotion,
  deletePromotion,
  getAdminProducts,
  getPromotions,
  updatePromotion,
} from '../services/api';
import './AdminPromotions.css';

const CATEGORIES = ['Electronics', 'Clothing', 'Books', 'Home & Garden', 'Sports', 'Toys', 'Collectibles', 'Other'];
const EMPTY_FORM = {
  name: '',
  headline: '',
  description: '',
  discountPercent: '15',
  targetType: 'all',
  targetValue: '',
  startsAt: '',
  endsAt: '',
  active: true,
};

function toDateTimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

function getPromotionStatus(promotion) {
  const now = Date.now();
  const startsAt = promotion.starts_at ? new Date(promotion.starts_at).getTime() : null;
  const endsAt = promotion.ends_at ? new Date(promotion.ends_at).getTime() : null;

  if (!promotion.active) return { label: 'Paused', className: 'paused' };
  if (startsAt && startsAt > now) return { label: 'Scheduled', className: 'scheduled' };
  if (endsAt && endsAt < now) return { label: 'Ended', className: 'ended' };
  return { label: 'Live', className: 'live' };
}

function targetLabel(promotion, productsById) {
  if (promotion.target_type === 'all') return 'Entire store';
  if (promotion.target_type === 'category') return promotion.target_value;
  if (promotion.target_type === 'product') {
    return productsById.get(String(promotion.target_value)) || `Product #${promotion.target_value}`;
  }
  return 'Custom';
}

export default function AdminPromotions() {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const productsById = useMemo(
    () => new Map(products.map((product) => [String(product.id), product.title])),
    [products]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [promotionRes, productRes] = await Promise.all([getPromotions(), getAdminProducts()]);
      setPromotions(promotionRes.data);
      setProducts(productRes.data.filter((product) => product.active));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load promotions right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const beginEdit = (promotion) => {
    setEditingId(promotion.id);
    setForm({
      name: promotion.name || '',
      headline: promotion.headline || '',
      description: promotion.description || '',
      discountPercent: String(promotion.discount_percent || 15),
      targetType: promotion.target_type || 'all',
      targetValue: promotion.target_value || '',
      startsAt: toDateTimeLocal(promotion.starts_at),
      endsAt: toDateTimeLocal(promotion.ends_at),
      active: Boolean(promotion.active),
    });
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    const payload = {
      ...form,
      startsAt: form.startsAt || null,
      endsAt: form.endsAt || null,
    };

    try {
      if (editingId) {
        await updatePromotion(editingId, payload);
        setSuccess('Promotion updated.');
      } else {
        await createPromotion(payload);
        setSuccess('Promotion created.');
      }

      resetForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this promotion.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (promotion) => {
    setError('');
    setSuccess('');

    try {
      await updatePromotion(promotion.id, {
        name: promotion.name,
        headline: promotion.headline || '',
        description: promotion.description || '',
        discountPercent: promotion.discount_percent,
        targetType: promotion.target_type,
        targetValue: promotion.target_value || '',
        startsAt: promotion.starts_at ? toDateTimeLocal(promotion.starts_at) : null,
        endsAt: promotion.ends_at ? toDateTimeLocal(promotion.ends_at) : null,
        active: !promotion.active,
      });
      setSuccess(`Promotion ${promotion.active ? 'paused' : 'activated'}.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update promotion status.');
    }
  };

  const handleDelete = async (promotion) => {
    if (!window.confirm(`Delete "${promotion.name}"?`)) return;

    setError('');
    setSuccess('');

    try {
      await deletePromotion(promotion.id);
      if (editingId === promotion.id) resetForm();
      setSuccess('Promotion deleted.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete promotion.');
    }
  };

  const livePromotions = promotions.filter((promotion) => getPromotionStatus(promotion).label === 'Live');

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      <div className="page-header promotions-header">
        <div>
          <h1>Promos &amp; Sales</h1>
          <p>Create storewide, category, or item-specific sales that update storefront pricing automatically.</p>
        </div>
        <div className="promotions-summary-badges">
          <span>{livePromotions.length} live</span>
          <span>{promotions.length} total</span>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="promotions-layout">
        <form className="card promotion-form-card" onSubmit={handleSave}>
          <div className="promotion-form-heading">
            <div>
              <span className="catalog-kicker">Campaign editor</span>
              <h2>{editingId ? 'Update promotion' : 'Launch a new promotion'}</h2>
            </div>
            {editingId && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Promotion name</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
              placeholder="Spring refresh sale"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Storefront headline</label>
            <input
              className="form-control"
              value={form.headline}
              onChange={(e) => setForm((current) => ({ ...current, headline: e.target.value }))}
              placeholder="Save 15% on every collectible this week"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
              placeholder="Use this for your own notes or the customer-facing message."
            />
          </div>

          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Discount percent</label>
              <input
                className="form-control"
                type="number"
                min="1"
                max="100"
                value={form.discountPercent}
                onChange={(e) => setForm((current) => ({ ...current, discountPercent: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Apply to</label>
              <select
                className="form-control"
                value={form.targetType}
                onChange={(e) => setForm((current) => ({
                  ...current,
                  targetType: e.target.value,
                  targetValue: '',
                }))}
              >
                <option value="all">Entire store</option>
                <option value="category">One category</option>
                <option value="product">One product</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={form.active ? 'live' : 'paused'}
                onChange={(e) => setForm((current) => ({ ...current, active: e.target.value === 'live' }))}
              >
                <option value="live">Live</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          {form.targetType === 'category' && (
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-control"
                value={form.targetValue}
                onChange={(e) => setForm((current) => ({ ...current, targetValue: e.target.value }))}
                required
              >
                <option value="">Choose category...</option>
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          )}

          {form.targetType === 'product' && (
            <div className="form-group">
              <label className="form-label">Product</label>
              <select
                className="form-control"
                value={form.targetValue}
                onChange={(e) => setForm((current) => ({ ...current, targetValue: e.target.value }))}
                required
              >
                <option value="">Choose product...</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Starts</label>
              <input
                className="form-control"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm((current) => ({ ...current, startsAt: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Ends</label>
              <input
                className="form-control"
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => setForm((current) => ({ ...current, endsAt: e.target.value }))}
              />
            </div>
          </div>

          <div className="promotion-form-actions">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving promotion...' : editingId ? 'Save promotion' : 'Launch promotion'}
            </button>
            <p>Sales update storefront pricing automatically. Checkout totals will follow the live promotion rules too.</p>
          </div>
        </form>

        <div className="promotion-list-column">
          <div className="card promotion-list-card">
            <div className="promotion-form-heading">
              <div>
                <span className="catalog-kicker">Current campaigns</span>
                <h2>Manage active, scheduled, and paused promos</h2>
              </div>
            </div>

            {loading ? (
              <div className="loading-center"><div className="spinner" /></div>
            ) : promotions.length === 0 ? (
              <div className="promotion-empty-state">
                <strong>No promotions yet.</strong>
                <p>Create your first sale to start highlighting discounted pricing around the store.</p>
              </div>
            ) : (
              <div className="promotion-list">
                {promotions.map((promotion) => {
                  const status = getPromotionStatus(promotion);

                  return (
                    <div key={promotion.id} className="promotion-card">
                      <div className="promotion-card-head">
                        <div>
                          <div className="promotion-chip-row">
                            <span className={`promotion-status ${status.className}`}>{status.label}</span>
                            <span className="promotion-discount">{promotion.discount_percent}% off</span>
                          </div>
                          <h3>{promotion.name}</h3>
                          <p>{promotion.headline || promotion.description || 'No storefront message yet.'}</p>
                        </div>
                      </div>

                      <div className="promotion-meta-grid">
                        <div>
                          <span>Target</span>
                          <strong>{targetLabel(promotion, productsById)}</strong>
                        </div>
                        <div>
                          <span>Starts</span>
                          <strong>{promotion.starts_at ? new Date(promotion.starts_at).toLocaleString() : 'Now'}</strong>
                        </div>
                        <div>
                          <span>Ends</span>
                          <strong>{promotion.ends_at ? new Date(promotion.ends_at).toLocaleString() : 'No end date'}</strong>
                        </div>
                      </div>

                      <div className="promotion-card-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => beginEdit(promotion)}>
                          Edit
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleToggle(promotion)}>
                          {promotion.active ? 'Pause' : 'Activate'}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(promotion)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
